import {useEffect, useMemo, useState} from 'react'
import {
  AlertCircle,
  CheckCircle2,
  Globe2,
  Link2,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
} from 'lucide-react'

import * as API from '../../../api'

const domainPattern = /^(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/
const normalizeDomains = (domains: string[]) =>
  Array.from(
    new Set(domains.map((domain) => domain.trim().toLocaleLowerCase('pt-BR')).filter(Boolean))
  )

const CompanyConfig = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [currentDomains, setCurrentDomains] = useState<string[]>([])
  const [domains, setDomains] = useState<string[]>([''])
  const [feedback, setFeedback] = useState('')

  async function getData() {
    setLoading(true)
    setLoadError(false)

    try {
      const response = await API.getCompanyDomain()
      const initialDomains = response?.data?.domain
        ? response.data.domain
            .split(',')
            .map((domain: string) => domain.trim())
            .filter(Boolean)
        : []
      setCurrentDomains(initialDomains)
      setDomains(initialDomains.length ? initialDomains : [''])
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getData()
  }, [])

  const invalidIndexes = useMemo(
    () =>
      domains.reduce<number[]>((indexes, domain, index) => {
        if (domain.trim() && !domainPattern.test(domain.trim())) indexes.push(index)
        return indexes
      }, []),
    [domains]
  )
  const normalizedDomains = normalizeDomains(domains)
  const normalizedCurrentDomains = normalizeDomains(currentDomains)
  const hasChanges = JSON.stringify(normalizedDomains) !== JSON.stringify(normalizedCurrentDomains)
  const isValid = invalidIndexes.length === 0

  async function handleSubmit() {
    if (!isValid || !hasChanges || saving) return
    setSaving(true)
    setFeedback('')

    try {
      const response = await API.updateDomain(normalizedDomains.join(','))
      if (response?.success === false) throw new Error('Domain update failed')
      setCurrentDomains(normalizedDomains)
      setDomains(normalizedDomains.length ? normalizedDomains : [''])
      setFeedback('Domínios atualizados com sucesso.')
    } catch {
      setFeedback('Não foi possível atualizar os domínios. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  function changeDomain(value: string, index: number) {
    setDomains((current) =>
      current.map((domain, itemIndex) => (itemIndex === index ? value : domain))
    )
    setFeedback('')
  }

  function removeDomain(index: number) {
    setDomains((current) => {
      const next = current.filter((_, itemIndex) => itemIndex !== index)
      return next.length ? next : ['']
    })
    setFeedback('')
  }

  return (
    <section className='ua-section'>
      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <Globe2 size={20} />
          </span>
          <div>
            <h2>Configurações de domínio</h2>
            <p>Defina os domínios reconhecidos para acesso e criação de usuários da empresa.</p>
          </div>
        </div>
        <span className='ua-badge ua-badge--warning'>Acesso de superadministrador</span>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando configurações…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Globe2 size={22} />
            </span>
            <h3>Não foi possível carregar os domínios</h3>
            <p>Tente novamente para acessar as configurações da empresa.</p>
            <button type='button' className='ua-secondary-button mt-4' onClick={getData}>
              Tentar novamente
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Globe2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{normalizedDomains.length}</strong>
                <small className='ua-stat__label'>Domínios configurados</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <CheckCircle2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{isValid ? 'Válido' : 'Revisar'}</strong>
                <small className='ua-stat__label'>Validação dos endereços</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <ShieldCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{hasChanges ? 'Pendente' : 'Salvo'}</strong>
                <small className='ua-stat__label'>Estado das alterações</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Link2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{invalidIndexes.length}</strong>
                <small className='ua-stat__label'>Endereços com erro</small>
              </span>
            </div>
          </div>

          {feedback && (
            <div
              className={`ua-feedback ${
                feedback.startsWith('Não foi possível') ? 'ua-feedback--danger' : ''
              }`}
            >
              {feedback}
            </div>
          )}

          <div className='ua-card ca-config-card'>
            <div className='ua-card__header'>
              <div>
                <h3>Domínios permitidos</h3>
                <p>Informe somente o domínio, sem protocolo ou caminho. Ex.: empresa.com.br</p>
              </div>
              <button
                type='button'
                className='ua-secondary-button'
                onClick={() => setDomains((current) => [...current, ''])}
              >
                <Plus size={14} /> Adicionar domínio
              </button>
            </div>

            <div className='ca-domain-list'>
              {domains.map((domain, index) => {
                const invalid = invalidIndexes.includes(index)
                return (
                  <div className='ca-domain-row' key={`domain-${index}`}>
                    <span className='ca-domain-row__icon'>
                      <Globe2 size={17} />
                    </span>
                    <div>
                      <input
                        value={domain}
                        className={invalid ? 'is-invalid' : ''}
                        aria-label={`Domínio ${index + 1}`}
                        aria-invalid={invalid}
                        placeholder='empresa.com.br'
                        onChange={(event) => changeDomain(event.target.value, index)}
                      />
                      {invalid && (
                        <span className='ca-company-modal__hint'>
                          Informe um domínio válido, sem “https://” ou barras.
                        </span>
                      )}
                    </div>
                    <button
                      type='button'
                      className='ua-icon-button ua-icon-button--danger'
                      aria-label={`Remover domínio ${index + 1}`}
                      title='Remover domínio'
                      onClick={() => removeDomain(index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              })}
            </div>

            {!isValid && (
              <div className='ua-feedback ua-feedback--danger mx-4 mb-0'>
                <AlertCircle size={14} /> Revise os domínios destacados antes de salvar.
              </div>
            )}

            <footer className='ca-config-actions'>
              <span>
                Você também pode salvar a lista vazia para não restringir o cadastro por domínio.
              </span>
              <button
                type='button'
                className='ua-primary-button'
                disabled={!isValid || !hasChanges || saving}
                onClick={handleSubmit}
              >
                {saving ? <span className='ua-spinner' /> : <Save size={14} />}
                Salvar configurações
              </button>
            </footer>
          </div>
        </>
      )}
    </section>
  )
}

export default CompanyConfig
